import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Calculator,
  ShieldCheck,
  Clock,
  Phone,
  Mail,
  ExternalLink,
} from "lucide-react";
import { EDGE, EDGE_LOGO, EDGE_NAVY, EDGE_NAVY_DARK, EDGE_APP_URL } from "../../lib/edge";
import MortgageDisclosure from "../../components/MortgageDisclosure";
import Seo from "../../components/Seo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=2000&q=80";

const PROGRAMS = [
  { name: "Conventional", short: "3% down options", to: "/loan-programs#conventional" },
  { name: "FHA", short: "3.5% down · flexible credit", to: "/loan-programs#fha" },
  { name: "VA", short: "0% down for veterans", to: "/loan-programs#va" },
  { name: "Jumbo", short: "Loans above conforming limits", to: "/loan-programs#jumbo" },
  { name: "USDA", short: "Rural / suburban · 0% down", to: "/loan-programs#usda" },
  { name: "First-Time Buyer", short: "Down-payment assistance", to: "/loan-programs#first-time" },
  { name: "FHA 203(k) Rehab", short: "Buy + renovate in one loan", to: "/loan-programs#203k" },
  { name: "DSCR", short: "Qualify on rental cash flow", to: "/loan-programs#dscr" },
  { name: "Investor Loans", short: "Bank statement · Fix & flip · Portfolio", to: "/loan-programs#investor" },
  { name: "Reverse Mortgage", short: "Age 62+ · tap equity, no payment", to: "/loan-programs#reverse" },
  { name: "Refinance", short: "Lower rate · cash-out · streamline", to: "/loan-programs#refinance" },
  { name: "HELOC / Home Equity", short: "Tap equity without touching 1st", to: "/loan-programs#heloc" },
  { name: "Construction-to-Permanent", short: "Build + mortgage in one close", to: "/loan-programs#construction" },
  { name: "Physician Loan", short: "MDs · 0–10% down · no PMI", to: "/loan-programs#physician" },
  { name: "ITIN Loan", short: "No SSN required · ITIN accepted", to: "/loan-programs#itin" },
  { name: "Refinance", short: "Rate-and-term refi", to: "/refinance" },
  { name: "Cash-Out Refi", short: "Tap your home equity", to: "/refinance#cash-out" },
];

const STEPS = [
  {
    n: "01",
    title: "Get pre-approved",
    text: "Tell us about your goals. We'll review credit, income, and assets — typically within 24 hours.",
  },
  {
    n: "02",
    title: "Find the right program",
    text: "Pierre walks you through your loan options and recommends the best fit for your situation.",
  },
  {
    n: "03",
    title: "Tour homes with confidence",
    text: "Pre-approval letter in hand, shop with your Gens Realty agent (Pierre is one too) knowing your budget.",
  },
  {
    n: "04",
    title: "Close on your home",
    text: "Lock your rate. We coordinate appraisal, underwriting, and closing — no surprises.",
  },
];

export default function MortgageHome() {
  return (
    <div data-testid="mortgage-home">
      <Seo
        title="Houston Mortgages · Edge Home Finance"
        description="Get pre-approved for your Houston home with Edge Home Finance. 15 loan programs including Conventional, FHA, VA, Jumbo, USDA, First-Time Buyer, FHA 203(k), DSCR, Investor, Reverse (HECM), Refinance, HELOC, Construction-to-Permanent, Physician, and ITIN. Pierre Joseph, NMLS #1978260."
        path="/mortgage"
      />

      {/* HERO */}
      <section className="relative h-[78vh] min-h-[560px] w-full overflow-hidden">
        <img src={HERO_IMG} alt="Modern home keys" className="absolute inset-0 w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${EDGE_NAVY_DARK}EE 0%, ${EDGE_NAVY}AA 60%, ${EDGE_NAVY}55 100%)`,
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white rounded-md p-2">
                <img src={EDGE_LOGO} alt="Edge Home Finance" className="h-9 w-auto" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/85">
                Edge Home Finance · NMLS #{EDGE.nmls}
              </div>
            </div>
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tighter max-w-4xl">
              Your Houston home loan, <br />
              done <span className="font-semibold" style={{ color: "#93C5FD" }}>right.</span>
            </h1>
            <p className="text-white/90 max-w-xl mt-6 text-lg leading-relaxed">
              Pierre Joseph is a licensed Realtor with Gens Realty <em>and</em> a Loan Officer with Edge
              Home Finance — you get one trusted person from search to closing.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a
                href={EDGE_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white hover:bg-stone-100 text-[#1A1A1A] px-7 py-3.5 rounded-md font-semibold text-sm transition-colors inline-flex items-center gap-2"
                data-testid="mortgage-hero-cta"
              >
                Apply Online (1003) <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to="/pre-approval"
                className="border border-white/40 hover:border-white text-white px-7 py-3.5 rounded-md font-semibold text-sm transition-colors"
              >
                Or leave a quick inquiry
              </Link>
              <a
                href={`tel:${EDGE.officer.phone.replace(/\D/g, "")}`}
                className="border border-white/40 hover:border-white text-white px-7 py-3.5 rounded-md font-semibold text-sm transition-colors flex items-center gap-2"
              >
                <Phone className="w-4 h-4" /> {EDGE.officer.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          <Trust icon={<Clock className="w-5 h-5" />} title="24-hr Pre-Approval" text="Most decisions same-day" />
          <Trust icon={<ShieldCheck className="w-5 h-5" />} title="NMLS Licensed" text={`#${EDGE.nmls} · #${EDGE.officer.nmls}`} />
          <Trust icon={<Calculator className="w-5 h-5" />} title="No Hidden Fees" text="Transparent loan estimates" />
          <Trust icon={<CheckCircle2 className="w-5 h-5" />} title="One Point of Contact" text="Pierre handles both sides" />
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: EDGE_NAVY }}>
          Loan Programs
        </div>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-[#1A1A1A]">
            Built for every kind of buyer.
          </h2>
          <Link to="/loan-programs" className="text-sm font-semibold text-[#1A1A1A] hover:text-[#E63946] flex items-center gap-2">
            All programs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {PROGRAMS.map((p) => (
            <Link
              key={p.name}
              to={p.to}
              className="group bg-white border border-gray-200 rounded-xl p-7 card-lift"
              data-testid={`program-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-5"
                style={{ background: `${EDGE_NAVY}1A`, color: EDGE_NAVY }}
              >
                <CheckCircle2 className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="font-display text-xl font-semibold text-[#1A1A1A]">{p.name}</div>
              <div className="text-sm text-[#555] mt-1.5">{p.short}</div>
              <div className="text-xs font-semibold mt-5 flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: EDGE_NAVY }}>
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="text-white" style={{ background: EDGE_NAVY_DARK }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: "#93C5FD" }}>
            How it works
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tighter max-w-2xl">
            From pre-approval to keys — in four steps.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
            {STEPS.map((s) => (
              <div key={s.n}>
                <div className="font-display text-5xl font-light tracking-tighter" style={{ color: "#93C5FD" }}>{s.n}</div>
                <div className="font-display text-xl font-semibold mt-4">{s.title}</div>
                <p className="text-sm text-white/75 leading-relaxed mt-2.5">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LO PROFILE TEASER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          <div
            className="lg:col-span-5 p-10 lg:p-14 text-white flex flex-col justify-between"
            style={{ background: EDGE_NAVY }}
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-3">
                Your loan officer
              </div>
              <div className="font-display text-4xl font-semibold">{EDGE.officer.name}</div>
              <div className="text-sm text-white/85 mt-1">{EDGE.officer.title}</div>
              <p className="text-sm text-white/80 mt-6 leading-relaxed">
                Pierre is a licensed Texas Realtor (#{EDGE.officer.realtorNumber}) with Gens Realty <em>and</em> a
                licensed Loan Officer with Edge Home Finance. One person — both sides of the table.
              </p>
            </div>
            <div className="mt-10 space-y-2 text-sm">
              <a href={`tel:${EDGE.officer.phone.replace(/\D/g, "")}`} className="flex items-center gap-3 hover:underline">
                <Phone className="w-4 h-4" /> {EDGE.officer.phone}
              </a>
              <a href={`mailto:${EDGE.officer.email}`} className="flex items-center gap-3 hover:underline">
                <Mail className="w-4 h-4" /> {EDGE.officer.email}
              </a>
            </div>
          </div>
          <div className="lg:col-span-7 p-10 lg:p-14">
            <h3 className="font-display text-3xl font-semibold tracking-tight">
              Ready to know what you can afford?
            </h3>
            <p className="text-[#555] mt-4 leading-relaxed">
              Get a no-pressure pre-approval — usually within 24 hours. No social-security number required to start.
              We'll review options and put you in the strongest position to make an offer.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <a
                href={EDGE_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#E63946] hover:bg-[#c9313d] text-white px-7 py-3.5 rounded-md font-semibold text-sm inline-flex items-center gap-2"
                data-testid="mortgage-lo-cta"
              >
                Apply Online (1003) <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <MortgageDisclosure />
    </div>
  );
}

function Trust({ icon, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${EDGE_NAVY}1A`, color: EDGE_NAVY }}
      >
        {icon}
      </div>
      <div>
        <div className="font-display font-semibold text-[#1A1A1A]">{title}</div>
        <div className="text-xs text-gray-500 mt-0.5">{text}</div>
      </div>
    </div>
  );
}
